/**
 * OrphanRecoveryTab — Site Admin > Rivalry Ops > Orphan Recovery.
 *
 * Surfaces leagues with no admin_email set (post-backfill orphans).
 * Site Admin can:
 *   - Assign an admin_email to recover the league
 *   - Archive (soft-delete) the league
 *
 * iter87.78 — companion to admin_email_backfill migration.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { AlertTriangle, UserPlus, Archive, Users, Trophy, Loader2, RefreshCw, Send, Search, Trash2, CheckSquare, Square } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

function useAdminClient() {
    const [client] = useState(() => {
        const c = axios.create({ baseURL: `${BASE}/api`, timeout: 15000 });
        c.interceptors.request.use((cfg) => {
            const session = JSON.parse(localStorage.getItem("pickem_admin_user") || "null");
            if (session?.token) cfg.headers.Authorization = `Bearer ${session.token}`;
            return cfg;
        });
        return c;
    });
    return client;
}

export default function OrphanRecoveryTab() {
    const client = useAdminClient();
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [assignFor, setAssignFor] = useState(null); // {id, name}
    const [archiveFor, setArchiveFor] = useState(null); // {id, name}
    const [view, setView] = useState("active"); // "active" | "archived"
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(() => new Set());
    const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { archived: view === "archived" };
            if (query.trim()) params.q = query.trim();
            const r = await client.get("/site-admin/orphans", { params });
            setItems(r.data.items || []);
            setCount(r.data.count || 0);
            setSelected(new Set());  // reset selection on any reload
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load orphans");
        }
        setLoading(false);
    }, [client, view, query]);

    useEffect(() => { load(); }, [load]);

    const toggleSel = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        setSelected((prev) => (prev.size === items.length ? new Set() : new Set(items.map((i) => i.id))));
    };

    const selCount = selected.size;
    const allSelected = selCount > 0 && selCount === items.length;

    return (
        <div className="space-y-4" data-testid="orphan-recovery-tab">
            <TransferOwnershipCard client={client} />

            <div className="flex items-baseline gap-2 flex-wrap">
                <h2 className="font-display font-black text-lg tracking-tight uppercase">
                    Orphan Recovery
                </h2>
                <span className="text-xs text-zinc-500 uppercase tracking-wider">
                    {count} {view === "archived" ? "archived" : "active"} orphan{count === 1 ? "" : "s"}
                </span>
                <button
                    type="button"
                    onClick={load}
                    data-testid="orphan-refresh-btn"
                    className="brutal-btn-ghost text-xs py-1 ml-auto"
                >
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {/* View toggle + search */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="flex border-2 border-[#333] rounded" data-testid="orphan-view-toggle">
                    <button
                        type="button"
                        onClick={() => setView("active")}
                        data-testid="orphan-view-active"
                        className={`text-xs px-3 py-1 font-bold uppercase tracking-wider ${
                            view === "active" ? "bg-cyber text-black" : "text-zinc-400 hover:text-white"
                        }`}
                    >
                        Active
                    </button>
                    <button
                        type="button"
                        onClick={() => setView("archived")}
                        data-testid="orphan-view-archived"
                        className={`text-xs px-3 py-1 font-bold uppercase tracking-wider ${
                            view === "archived" ? "bg-cyber text-black" : "text-zinc-400 hover:text-white"
                        }`}
                    >
                        Archived
                    </button>
                </div>
                <div className="flex items-center gap-1 flex-1 max-w-[280px]">
                    <Search className="w-3 h-3 text-zinc-500 shrink-0" />
                    <input
                        data-testid="orphan-search-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="brutal-input text-xs py-1 w-full"
                        placeholder="Search rivalry name…"
                    />
                </div>
                {selCount > 0 && (
                    <>
                        <span className="text-xs text-cyber font-bold uppercase tracking-wider" data-testid="orphan-selection-count">
                            {selCount} selected
                        </span>
                        {view === "active" && (
                            <button
                                type="button"
                                onClick={() => setBulkArchiveOpen(true)}
                                data-testid="orphan-bulk-archive-btn"
                                className="brutal-btn-ghost text-xs py-1"
                            >
                                <Archive className="w-3 h-3" /> Archive {selCount}
                            </button>
                        )}
                        {view === "archived" && (
                            <button
                                type="button"
                                onClick={() => setBulkDeleteOpen(true)}
                                data-testid="orphan-bulk-delete-btn"
                                className="brutal-btn-ghost text-xs py-1 !text-red-400 !border-red-500/40"
                            >
                                <Trash2 className="w-3 h-3" /> Delete {selCount}
                            </button>
                        )}
                    </>
                )}
            </div>

            <div className="brutal-card-light p-3 text-[11px] text-zinc-400 flex gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <p>
                    {view === "active" ? (
                        <>
                            These rivalries have no <code className="text-cyber">admin_email</code> on file. Assign an admin,
                            archive them individually, or select multiple and bulk-archive.
                        </>
                    ) : (
                        <>
                            These orphans are already archived. Select any to <strong className="text-red-400">permanently delete</strong>{" "}
                            them and their participants, tournaments, picks, and memberships. This cannot be undone — an audit
                            row is written before the delete.
                        </>
                    )}
                </p>
            </div>

            {loading ? (
                <div className="text-center py-8 text-zinc-500">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
            ) : items.length === 0 ? (
                <div className="brutal-card p-8 text-center border-dashed">
                    <p className="text-sm text-zinc-400">
                        {query.trim()
                            ? `No ${view} orphans match "${query.trim()}".`
                            : view === "active"
                                ? "No orphaned rivalries. Every league has an admin on file."
                                : "No archived orphans."}
                    </p>
                </div>
            ) : (
                <div className="space-y-2" data-testid="orphan-list">
                    <button
                        type="button"
                        onClick={toggleAll}
                        data-testid="orphan-toggle-all"
                        className="brutal-btn-ghost text-xs py-1"
                    >
                        {allSelected ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                        {allSelected ? "Deselect all" : `Select all ${items.length}`}
                    </button>
                    {items.map((lg) => (
                        <OrphanRow
                            key={lg.id}
                            league={lg}
                            view={view}
                            selected={selected.has(lg.id)}
                            onToggleSelect={() => toggleSel(lg.id)}
                            onAssign={() => setAssignFor({ id: lg.id, name: lg.name })}
                            onArchive={() => setArchiveFor({ id: lg.id, name: lg.name })}
                        />
                    ))}
                </div>
            )}

            {assignFor && (
                <AssignModal
                    league={assignFor}
                    client={client}
                    onClose={() => setAssignFor(null)}
                    onDone={() => { setAssignFor(null); load(); }}
                />
            )}
            {archiveFor && (
                <ArchiveModal
                    league={archiveFor}
                    client={client}
                    onClose={() => setArchiveFor(null)}
                    onDone={() => { setArchiveFor(null); load(); }}
                />
            )}
            {bulkArchiveOpen && (
                <BulkActionModal
                    kind="archive"
                    ids={Array.from(selected)}
                    itemsById={Object.fromEntries(items.map((i) => [i.id, i.name]))}
                    client={client}
                    onClose={() => setBulkArchiveOpen(false)}
                    onDone={() => { setBulkArchiveOpen(false); load(); }}
                />
            )}
            {bulkDeleteOpen && (
                <BulkActionModal
                    kind="delete"
                    ids={Array.from(selected)}
                    itemsById={Object.fromEntries(items.map((i) => [i.id, i.name]))}
                    client={client}
                    onClose={() => setBulkDeleteOpen(false)}
                    onDone={() => { setBulkDeleteOpen(false); load(); }}
                />
            )}
        </div>
    );
}

function OrphanRow({ league, view, selected, onToggleSelect, onAssign, onArchive }) {
    return (
        <div
            className={`brutal-card-light p-4 flex flex-wrap items-center gap-3 ${selected ? "border-cyber/80 bg-cyber/5" : ""}`}
            data-testid={`orphan-row-${league.id}`}
        >
            <button
                type="button"
                onClick={onToggleSelect}
                data-testid={`orphan-select-${league.id}`}
                className="p-1 shrink-0"
                aria-label={selected ? "Deselect" : "Select"}
            >
                {selected ? <CheckSquare className="w-4 h-4 text-cyber" /> : <Square className="w-4 h-4 text-zinc-500" />}
            </button>
            <Trophy className="w-4 h-4 text-zinc-500 shrink-0" />
            <div className="flex-1 min-w-[180px]">
                <p className="font-display font-extrabold text-sm">{league.name}</p>
                <p className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5 flex-wrap">
                    <Users className="w-3 h-3" />
                    {league.participant_count ?? 0} participants
                    <span>·</span>
                    {league.active_tournaments ?? 0} tournament{(league.active_tournaments ?? 0) === 1 ? "" : "s"} live
                    {league.has_any_admin_participant && league.admin_participant_username && (
                        <>
                            <span>·</span>
                            <span className="text-amber-500">
                                admin participant: <strong>{league.admin_participant_username}</strong> (no email)
                            </span>
                        </>
                    )}
                    {view === "archived" && league.deleted_at && (
                        <>
                            <span>·</span>
                            <span className="text-red-400">
                                archived{league.archived_by ? ` by ${league.archived_by}` : ""}
                            </span>
                        </>
                    )}
                </p>
            </div>
            {view === "active" && (
                <>
                    <button
                        type="button"
                        onClick={onAssign}
                        data-testid={`orphan-assign-btn-${league.id}`}
                        className="brutal-btn-primary text-xs py-1"
                    >
                        <UserPlus className="w-3 h-3" /> Assign admin
                    </button>
                    <button
                        type="button"
                        onClick={onArchive}
                        data-testid={`orphan-archive-btn-${league.id}`}
                        className="brutal-btn-ghost text-xs py-1"
                    >
                        <Archive className="w-3 h-3" /> Archive
                    </button>
                </>
            )}
        </div>
    );
}

function AssignModal({ league, client, onClose, onDone }) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim().includes("@")) { toast.error("Valid email required"); return; }
        setBusy(true);
        try {
            await client.post(`/site-admin/orphans/${league.id}/assign`, { admin_email: email.trim().toLowerCase() });
            toast.success(`${league.name} assigned to ${email.trim().toLowerCase()}`);
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Assign failed");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}
            data-testid="orphan-assign-modal">
            <div className="brutal-card w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                <h2 className="font-display font-black text-lg uppercase tracking-tight mb-3">Assign Admin</h2>
                <p className="text-xs text-zinc-400 mb-3">
                    Set <strong className="text-white">{league.name}</strong>'s admin_email. That person becomes the owner
                    and can see this rivalry in their Pro+ dashboard.
                </p>
                <form onSubmit={submit} className="space-y-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Admin email</span>
                        <input
                            data-testid="orphan-assign-email"
                            type="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="brutal-input text-sm w-full mt-1"
                            placeholder="new-admin@example.com"
                        />
                    </label>
                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose} className="brutal-btn-ghost text-xs py-2 flex-1">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="orphan-assign-submit"
                            className="brutal-btn-primary text-xs py-2 flex-1 disabled:opacity-40"
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                            Assign
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ArchiveModal({ league, client, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (reason.trim().length < 3) { toast.error("Please add a short reason"); return; }
        setBusy(true);
        try {
            await client.post(`/site-admin/orphans/${league.id}/archive`, { reason: reason.trim() });
            toast.success(`${league.name} archived`);
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Archive failed");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}
            data-testid="orphan-archive-modal">
            <div className="brutal-card w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                <h2 className="font-display font-black text-lg uppercase tracking-tight mb-3">Archive Rivalry</h2>
                <p className="text-xs text-zinc-400 mb-3">
                    Soft-deletes <strong className="text-white">{league.name}</strong>. Reason is logged for governance audit.
                </p>
                <form onSubmit={submit} className="space-y-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Reason</span>
                        <input
                            data-testid="orphan-archive-reason"
                            autoFocus
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="brutal-input text-sm w-full mt-1"
                            placeholder="e.g., abandoned by original owner"
                            maxLength={500}
                        />
                    </label>
                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose} className="brutal-btn-ghost text-xs py-2 flex-1">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="orphan-archive-submit"
                            className="brutal-btn-primary text-xs py-2 flex-1 disabled:opacity-40 !bg-amber-500 !border-amber-600"
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
                            Archive
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

/**
 * TransferOwnershipCard — operator-authority reassign for ANY league,
 * not just orphans. Two-step: lookup invite code, then confirm + supply
 * reason for the compliance audit trail.
 */
function TransferOwnershipCard({ client }) {
    const [inviteCode, setInviteCode] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [reason, setReason] = useState("");
    const [lookup, setLookup] = useState(null);
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const doLookup = async () => {
        const code = inviteCode.trim().toUpperCase();
        if (code.length < 3) { toast.error("Invite code required"); return; }
        setBusy(true);
        try {
            const r = await client.get(`/site-admin/leagues/lookup-by-invite/${code}`);
            setLookup(r.data);
        } catch (err) {
            setLookup(null);
            toast.error(err.response?.data?.detail || "No league found");
        }
        setBusy(false);
    };

    const doTransfer = async () => {
        if (!newEmail.trim().includes("@")) { toast.error("Valid new-owner email required"); return; }
        if (reason.trim().length < 3) { toast.error("Reason required for audit trail"); return; }
        setBusy(true);
        try {
            const r = await client.post(
                `/site-admin/leagues/${lookup.id}/transfer-ownership`,
                {
                    invite_code: inviteCode.trim().toUpperCase(),
                    new_admin_email: newEmail.trim().toLowerCase(),
                    reason: reason.trim(),
                },
            );
            toast.success(`Transferred ${r.data.league_name} → ${r.data.to_email}`);
            setInviteCode("");
            setNewEmail("");
            setReason("");
            setLookup(null);
            setExpanded(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Transfer failed");
        }
        setBusy(false);
    };

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="transfer-ownership-card">
            <div className="flex items-center gap-2">
                <Send className="w-4 h-4 text-cyber" />
                <h3 className="font-display font-black text-base uppercase tracking-tight">
                    Transfer Ownership
                </h3>
                {!expanded && (
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        data-testid="transfer-ownership-expand"
                        className="brutal-btn-ghost text-xs py-1 ml-auto"
                    >
                        Open form
                    </button>
                )}
                {expanded && (
                    <button
                        type="button"
                        onClick={() => { setExpanded(false); setLookup(null); }}
                        className="brutal-btn-ghost text-xs py-1 ml-auto"
                    >
                        Close
                    </button>
                )}
            </div>
            {!expanded ? (
                <p className="text-[11px] text-zinc-500">
                    Reassign any rivalry to a new admin email. Different from Orphan
                    Recovery — this works for leagues that already have an owner.
                </p>
            ) : (
                <div className="space-y-3">
                    <div className="flex gap-2 items-end">
                        <label className="block flex-1">
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Invite code</span>
                            <input
                                data-testid="transfer-invite-code"
                                value={inviteCode}
                                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                className="brutal-input text-sm w-full mt-1 uppercase"
                                placeholder="ABC123"
                                maxLength={20}
                                autoComplete="off"
                            />
                        </label>
                        <button
                            type="button"
                            onClick={doLookup}
                            disabled={busy}
                            data-testid="transfer-lookup-btn"
                            className="brutal-btn-primary text-xs py-2 disabled:opacity-40"
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                            Lookup
                        </button>
                    </div>
                    {lookup && (
                        <div className="brutal-card-light p-3 space-y-2" data-testid="transfer-lookup-result">
                            <p className="text-sm font-display font-extrabold">{lookup.name}</p>
                            <p className="text-[11px] text-zinc-500">
                                Current owner:{" "}
                                <strong className="text-zinc-300">
                                    {lookup.admin_email || <span className="text-amber-500">(none — orphan)</span>}
                                </strong>
                                <span> · Lifecycle: <span className="text-zinc-300">{lookup.lifecycle_state}</span></span>
                            </p>
                            <label className="block">
                                <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">New owner email</span>
                                <input
                                    data-testid="transfer-new-email"
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    className="brutal-input text-sm w-full mt-1"
                                    placeholder="new-owner@example.com"
                                />
                            </label>
                            <label className="block">
                                <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Reason (audit)</span>
                                <input
                                    data-testid="transfer-reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="brutal-input text-sm w-full mt-1"
                                    placeholder="e.g., previous admin left the team"
                                    maxLength={500}
                                />
                            </label>
                            <button
                                type="button"
                                onClick={doTransfer}
                                disabled={busy}
                                data-testid="transfer-submit"
                                className="brutal-btn-primary text-xs py-2 w-full disabled:opacity-40"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Transfer ownership
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


/**
 * BulkActionModal — bulk archive OR bulk hard-delete on selected orphan
 * IDs. Reason is required for both. Delete has an extra "type
 * confirmation" step (the count must match the ids length) mirrored on
 * the backend as a foot-gun guard.
 */
function BulkActionModal({ kind, ids, itemsById, client, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const isDelete = kind === "delete";
    const needsTypedConfirm = isDelete;
    const confirmPhrase = `DELETE ${ids.length}`;

    const canSubmit = reason.trim().length >= 3 && (!needsTypedConfirm || confirmText === confirmPhrase);

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            const endpoint = isDelete
                ? "/site-admin/orphans/bulk-delete"
                : "/site-admin/orphans/bulk-archive";
            const body = { ids, reason: reason.trim() };
            if (isDelete) body.confirm_count = ids.length;
            const r = await client.post(endpoint, body);
            const done = isDelete ? r.data.deleted_count : r.data.archived_count;
            const skipped = r.data.skipped_count;
            toast.success(
                `${isDelete ? "Deleted" : "Archived"} ${done} of ${ids.length}` +
                (skipped ? ` · ${skipped} skipped` : ""),
            );
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || `Bulk ${kind} failed`);
        }
        setBusy(false);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid={`orphan-bulk-${kind}-modal`}
        >
            <div className="brutal-card w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                <h2 className="font-display font-black text-lg uppercase tracking-tight mb-3">
                    {isDelete ? "Permanently delete" : "Archive"} {ids.length} rivalr{ids.length === 1 ? "y" : "ies"}
                </h2>
                {isDelete ? (
                    <div className="flex items-start gap-2 bg-red-500/5 border border-red-500/40 p-3 rounded mb-3">
                        <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                        <div className="text-xs text-zinc-300">
                            <p className="font-bold text-red-400 mb-1">This cannot be undone.</p>
                            <p>
                                Every participant, tournament, match, pick, and mobile membership tied to these rivalries
                                is deleted. An audit row is written to <code className="text-cyber">compliance_actions</code>{" "}
                                before the delete so the paper trail survives.
                            </p>
                        </div>
                    </div>
                ) : (
                    <p className="text-xs text-zinc-400 mb-3">
                        Soft-deletes the selected rivalries. They'll move to the Archived view and become
                        eligible for permanent deletion later.
                    </p>
                )}

                {/* Preview list — first 5 */}
                <div className="brutal-card-light p-2 mb-3 max-h-32 overflow-y-auto space-y-1" data-testid="orphan-bulk-preview">
                    {ids.slice(0, 5).map((id) => (
                        <p key={id} className="text-[11px] text-zinc-400 truncate">
                            · {itemsById[id] || id}
                        </p>
                    ))}
                    {ids.length > 5 && (
                        <p className="text-[10px] text-zinc-500 italic">…and {ids.length - 5} more</p>
                    )}
                </div>

                <label className="block mb-3">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Reason (audit)</span>
                    <input
                        data-testid="orphan-bulk-reason"
                        autoFocus
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="brutal-input text-sm w-full mt-1"
                        placeholder={isDelete ? "e.g., data-cleanup 2026-02" : "e.g., abandoned by original owners"}
                        maxLength={500}
                    />
                </label>

                {needsTypedConfirm && (
                    <label className="block mb-3">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
                            Type <code className="text-red-400">{confirmPhrase}</code> to confirm
                        </span>
                        <input
                            data-testid="orphan-bulk-typed-confirm"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            className="brutal-input text-sm w-full mt-1"
                            placeholder={confirmPhrase}
                            autoComplete="off"
                        />
                    </label>
                )}

                <div className="flex gap-2">
                    <button type="button" onClick={onClose} className="brutal-btn-ghost text-xs py-2 flex-1">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy || !canSubmit}
                        data-testid={`orphan-bulk-${kind}-submit`}
                        className={`text-xs py-2 flex-1 disabled:opacity-40 ${
                            isDelete
                                ? "brutal-btn-primary !bg-red-500 !border-red-600"
                                : "brutal-btn-primary !bg-amber-500 !border-amber-600"
                        }`}
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (isDelete ? <Trash2 className="w-3 h-3" /> : <Archive className="w-3 h-3" />)}
                        {isDelete ? "Delete permanently" : "Archive"}
                    </button>
                </div>
            </div>
        </div>
    );
}

